/**
 * LiveDispatchMap.jsx · v2.7.65+E53 · FollowMee KML edition (2026-07-12)
 * ═════════════════════════════════════════════════════════════════════
 * Full replacement of the legacy custom-GPS renderer (archived at
 * `LiveDispatchMap.legacy.jsx`).
 *
 * Data flow:
 *   Google Maps <── native markers <── parsed KML <── adminApi (auth)
 *     └── XML parsed client-side ────────────────────────┘
 *              ↑
 *              backend proxy (/api/admin/live-map/followmee-kml)
 *              ↑
 *              FollowMee tracks.aspx (username + key server-side only)
 *
 * We DO NOT use `google.maps.KmlLayer` because that layer is fetched
 * from Google's own servers, which can't send our admin auth header.
 * Instead we fetch via `adminApi`, parse the KML with DOMParser, and
 * render one native `google.maps.Marker` per placemark. Full 30s poll,
 * position updates in place (no marker recreation) to avoid flicker.
 *
 * Props (unchanged from the legacy signature):
 *   token             — supervisor JWT (unused here; auth flows through
 *                       adminApi which reads localStorage)
 *   active            — currently ignored; kept for API stability
 *   height            — CSS height of the outer wrapper (default "420px")
 *   fullscreenEnabled — show the Fullscreen toggle in the header
 *   isPopOutView      — auto-fit to bounds on first load
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { loadGoogleMaps } from "@/lib/loadGoogleMaps";
import { Input } from "@/components/ui/input";
import {
  Maximize2, Minimize2, ExternalLink, Users, LayoutPanelLeft, X,
  Crosshair, RefreshCw, TrafficCone, ArrowDownAZ, Radar,
} from "lucide-react";

const JSON_ENDPOINT  = "/admin/live-map/followmee-json"; // adminApi prefixes /api
// v2.7.65+E56 · Fast polling (3.5s) — FollowMee driver phones typically
// only ping fresh coords every 30-60s (phone-side GPS interval), so most
// polls will re-fetch identical positions. That's fine: we combine the
// fast poll with client-side rAF interpolation below so markers glide
// smoothly between the last two known positions, giving the visual
// effect of live movement even without a new upstream fix.
const POLL_MS        = 3_500;
const INTERP_MS      = 3_500; // marker glide window (matches poll cadence)
const DEFAULT_CENTER = { lat: 35.4676, lng: -97.5164 };  // OKC metro
const DEFAULT_ZOOM   = 10;

// Great-circle distance in km — used by the Roster's "Nearest to center" sort.
function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

export default function LiveDispatchMap({
  height = "420px",
  fullscreenEnabled = false,
  isPopOutView = false,
}) {
  const wrapperRef = useRef(null);
  const mapDivRef  = useRef(null);
  const mapRef     = useRef(null);
  const markersRef = useRef({});         // id → google.maps.Marker
  const infoRef    = useRef(null);       // reusable InfoWindow
  const pollRef    = useRef(null);
  const didFitRef  = useRef(false);      // one-shot fit-bounds on first load
  const trafficLayerRef = useRef(null);  // google.maps.TrafficLayer, lazily created
  // v2.7.65+E56 · rAF interpolation state per device
  //   id → { from:{lat,lng}, to:{lat,lng}, startedAt: DOMHighResTimeStamp }
  const interpStateRef = useRef({});
  const rafRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [status,  setStatus]  = useState("loading");   // loading | ready | empty | error
  const [message, setMessage] = useState("Starting live fleet feed…");
  const [vehicleCount, setVehicleCount] = useState(0);
  // v2.7.65+E53.1 · Fleet Roster side panel
  const [rosterOpen, setRosterOpen] = useState(!isPopOutView); // pop-out defaults hidden
  const [roster, setRoster] = useState([]);            // sanitized devices from JSON
  const [lastRefreshAt, setLastRefreshAt] = useState(0);
  const [tick, setTick] = useState(0);                 // 1Hz ticker for "N s ago"
  // v2.7.65+E54 · Roster filter + sort + map toolbar controls
  const [filterQuery, setFilterQuery] = useState("");
  const [sortMethod,  setSortMethod]  = useState("alpha");   // "alpha" | "distance"
  const [selectedGroup, setSelectedGroup] = useState("All"); // group filter for map + roster
  const [trafficLayerActive, setTrafficLayerActive] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER); // tracked for distance sort
  // v2.7.65+E56 · KPI drill-down state — "all" | "active" | "offline"
  const [statusFilter, setStatusFilter] = useState("all");
  // Device id currently being highlighted by a KPI drill-down click.
  // Auto-clears after 6s so the ring pulse doesn't linger forever.
  const [highlightId, setHighlightId] = useState("");
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(""), 6000);
    return () => clearTimeout(t);
  }, [highlightId]);

  // Bump `tick` once per second so the relative timestamps stay live.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const relLastRefresh = useMemo(() => relSeconds(lastRefreshAt), [lastRefreshAt, tick]);

  // Derived: unique group names for the map-toolbar dropdown.
  const uniqueGroups = useMemo(() => {
    const set = new Set();
    roster.forEach((d) => set.add(d.group || "Unassigned"));
    return Array.from(set).sort();
  }, [roster]);

  // Derived: group-scoped devices used both by the roster and by
  // marker-visibility toggling below.
  const groupScoped = useMemo(
    () => (selectedGroup === "All" ? roster : roster.filter((d) => d.group === selectedGroup)),
    [roster, selectedGroup],
  );

  // Derived: fully-processed roster list (filter + sort). See Part 1 spec.
  const processedRoster = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    const filtered = groupScoped.filter((d) => {
      if (q && !d.name.toLowerCase().includes(q)) return false;
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      return true;
    });
    if (sortMethod === "distance") {
      return [...filtered].sort(
        (a, b) => haversineKm(mapCenter, a) - haversineKm(mapCenter, b),
      );
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [groupScoped, filterQuery, sortMethod, mapCenter, statusFilter]);

  // v2.7.65+E55 · Fleet at a Glance KPIs — respect the group filter so
  // the strip reflects the current dispatcher scope, not the whole fleet.
  const fleetKpis = useMemo(() => {
    const scope = groupScoped;
    const total = scope.length;
    const active = scope.filter((d) => d.status === "active").length;

    // Battery arrives as "35%" strings — parse the leading number and
    // average across devices that reported one.
    const batteries = scope
      .map((d) => {
        if (!d.battery) return null;
        const m = String(d.battery).match(/-?\d+(\.\d+)?/);
        return m ? parseFloat(m[0]) : null;
      })
      .filter((n) => n != null && Number.isFinite(n));
    const avgBattery = batteries.length
      ? Math.round(batteries.reduce((s, n) => s + n, 0) / batteries.length)
      : null;

    // Longest silence = max age_seconds across the scope; devices that
    // never reported (age_seconds == null) don't count against the KPI.
    const ages = scope
      .map((d) => d.age_seconds)
      .filter((n) => n != null && Number.isFinite(n));
    const longestSilence = ages.length ? Math.max(...ages) : null;

    return { total, active, avgBattery, longestSilence };
  }, [groupScoped]);

  // ── Map bootstrap ───────────────────────────────────────────────────
  const refreshFnRef = useRef(null); // populated by the bootstrap effect below
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const maps = await loadGoogleMaps();
        if (cancelled || !mapDivRef.current) return;

        const map = new maps.Map(mapDivRef.current, {
          center:           DEFAULT_CENTER,
          zoom:             DEFAULT_ZOOM,
          disableDefaultUI: true,
          zoomControl:      true,
          scaleControl:     true,
          gestureHandling:  "greedy",
          mapTypeId:        maps.MapTypeId.ROADMAP,
        });
        mapRef.current = map;
        infoRef.current = new maps.InfoWindow();

        // Keep the tracked map center in sync so distance-sort stays honest.
        const emitCenter = () => {
          const c = map.getCenter();
          if (c) setMapCenter({ lat: c.lat(), lng: c.lng() });
        };
        maps.event.addListener(map, "idle", emitCenter);

        // v2.7.65+E56 · rAF interpolation loop — glides each marker from
        // its previous known position to the newest one over INTERP_MS
        // using an ease-out curve. Runs while the component is mounted.
        const step = (nowTs) => {
          const states = interpStateRef.current;
          for (const [id, s] of Object.entries(states)) {
            const marker = markersRef.current[id];
            if (!marker) continue;
            const t = Math.min(1, (nowTs - s.startedAt) / INTERP_MS);
            const eased = 1 - (1 - t) * (1 - t); // ease-out quadratic
            const lat = s.from.lat + (s.to.lat - s.from.lat) * eased;
            const lng = s.from.lng + (s.to.lng - s.from.lng) * eased;
            marker.setPosition({ lat, lng });
          }
          rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);

        // Expose the refresh function so the toolbar button can call it.
        refreshFnRef.current = () => refresh(maps);

        // Initial fetch + start polling.
        await refresh(maps);
        pollRef.current = setInterval(() => refresh(maps).catch(() => {}), POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage(e?.message || "Failed to load Google Maps");
      }
    })();

    async function refresh(maps) {
      let payload;
      setRefreshing(true);
      try {
        const resp = await adminApi.get(JSON_ENDPOINT, {
          params: { nocache: Date.now() },
        });
        payload = resp.data || {};
      } catch (e) {
        setStatus("error");
        setMessage(`Fetch failed: ${e?.response?.status || ""} ${e?.response?.data?.detail || e?.message || ""}`.trim());
        setRefreshing(false);
        return;
      }

      const devices = Array.isArray(payload.devices) ? payload.devices : [];
      setVehicleCount(devices.length);
      setRoster(devices);
      setLastRefreshAt(Date.now());

      // Reconcile markers — update in place, remove stale, add new. Color
      // follows the device's live status field (active = green, offline = red).
      const seenIds = new Set();
      const nowTs = (typeof performance !== "undefined" ? performance.now() : Date.now());
      for (const dev of devices) {
        seenIds.add(dev.id);
        const pos = { lat: dev.lat, lng: dev.lng };
        const fillColor = dev.status === "active" ? "#22c55e" : "#f43f5e";
        const strokeColor = dev.status === "active" ? "#052e16" : "#450a0a";
        const existing = markersRef.current[dev.id];
        // v2.7.65+E57 · Vehicle-heading arrow. If the device reports a
        // `direction` (0-360°) AND is active, render a rotated SVG
        // chevron; else fall back to a plain colored dot. Rotation uses
        // Google Maps' Symbol rotation prop (works on both Marker types).
        const hasHeading =
          dev.status === "active" && dev.direction != null && Number.isFinite(dev.direction);
        const iconSpec = hasHeading
          ? {
              path:         maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale:        4.5,
              fillColor,
              fillOpacity:  0.95,
              strokeColor,
              strokeWeight: 2,
              rotation:     dev.direction,   // degrees, 0 = north, clockwise
              labelOrigin:  new maps.Point(0, 24),
            }
          : {
              path:         maps.SymbolPath.CIRCLE,
              scale:        8,
              fillColor,
              fillOpacity:  0.9,
              strokeColor,
              strokeWeight: 2,
              labelOrigin:  new maps.Point(0, 24),
            };
        if (existing) {
          // v2.7.65+E56 · Kick off an rAF interpolation from the marker's
          // CURRENT interpolated position (not the last target — otherwise
          // the glide would "jump" if a poll arrives before the previous
          // glide finished).
          const cur = existing.getPosition();
          const curLL = cur ? { lat: cur.lat(), lng: cur.lng() } : pos;
          interpStateRef.current[dev.id] = {
            from:      curLL,
            to:        pos,
            startedAt: nowTs,
          };
          // Refresh icon (color + heading rotation) each poll.
          existing.setIcon(iconSpec);
        } else {
          const marker = new maps.Marker({
            position:  pos,
            map:       mapRef.current,
            title:     dev.name,
            optimized: false,
            label:     {
              text:      dev.name.length > 6 ? dev.name.slice(0, 6) : dev.name,
              color:     "#ffffff",
              fontSize:  "10px",
              fontWeight: "700",
            },
            icon: iconSpec,
          });
          marker.addListener("click", () => {
            openInfoFor(dev, marker);
          });
          markersRef.current[dev.id] = marker;
          // First-seen marker: `from == to`, so the glide is a no-op.
          interpStateRef.current[dev.id] = {
            from:      pos,
            to:        pos,
            startedAt: nowTs,
          };
        }
      }
      // Drop stale markers.
      for (const id of Object.keys(markersRef.current)) {
        if (!seenIds.has(id)) {
          markersRef.current[id].setMap(null);
          delete markersRef.current[id];
          delete interpStateRef.current[id];
        }
      }

      // One-shot fit-to-bounds when we're in pop-out mode.
      if (isPopOutView && !didFitRef.current && devices.length > 0 && mapRef.current) {
        const bounds = new maps.LatLngBounds();
        devices.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        mapRef.current.fitBounds(bounds, 40);
        didFitRef.current = true;
      }

      if (devices.length === 0) {
        setStatus("empty");
        setMessage("No fleet devices reported in the last update");
      } else {
        const activeN = devices.filter((d) => d.status === "active").length;
        setStatus("ready");
        setMessage(`Live · ${activeN}/${devices.length} active · polling every 3.5s`);
      }
      setRefreshing(false);
    }

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      interpStateRef.current = {};
      for (const id of Object.keys(markersRef.current)) {
        try { markersRef.current[id].setMap(null); } catch { /* ignore */ }
      }
      markersRef.current = {};
      try { infoRef.current?.close(); } catch { /* ignore */ }
      mapRef.current = null;
    };
  }, [isPopOutView]);

  // ── Fullscreen (HTML5 API, vendor-prefixed) ─────────────────────────
  useEffect(() => {
    const onChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(fs);
      if (mapRef.current && window.google?.maps?.event) {
        setTimeout(() => {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }, 220);
      }
    };
    document.addEventListener("fullscreenchange",       onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange",       onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const handleFullscreenToggle = () => {
    if (!wrapperRef.current) return;
    if (!document.fullscreenElement) {
      const req =
        wrapperRef.current.requestFullscreen ||
        wrapperRef.current.webkitRequestFullscreen ||
        wrapperRef.current.msRequestFullscreen;
      if (req) req.call(wrapperRef.current).catch(() => {});
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      if (exit) exit.call(document).catch(() => {});
    }
  };

  const handlePopOutWindow = () => {
    const w = 1280;
    const h = 800;
    const left = Math.max(0, Math.floor((window.screen.width  - w) / 2));
    const top  = Math.max(0, Math.floor((window.screen.height - h) / 2));
    window.open(
      "/admin/live-map-only?console=map",
      "TaxiCommanderLiveMap",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no,status=no`,
    );
  };

  const dotClass =
    status === "ready" ? "bg-emerald-500 animate-pulse" :
    status === "empty" ? "bg-amber-500"                :
    status === "error" ? "bg-rose-500"                 :
                         "bg-sky-500";

  const openInfoFor = useCallback((dev, marker) => {
    if (!infoRef.current || !mapRef.current) return;
    const ageMin = dev.age_seconds != null ? Math.floor(dev.age_seconds / 60) : null;
    const rows = [
      ["Group",       dev.group || "Unassigned"],
      ["Status",      dev.status === "active" ? "Active" : "Offline"],
      ["Last ping",   ageMin != null ? `${ageMin} min ago` : (dev.last_update || "—")],
      ["Speed",       dev.speed_mph != null ? `${dev.speed_mph} mph` : "—"],
      ["Heading",     dev.direction != null ? `${Math.round(dev.direction)}°` : "—"],
      ["Battery",     dev.battery || "—"],
      ["Coordinates", `${dev.lat.toFixed(5)}, ${dev.lng.toFixed(5)}`],
    ];
    const body = rows
      .map(
        ([k, v]) => `<tr>
          <td style="color:#64748b;padding-right:8px;font-weight:600;">${escapeHtml(k)}</td>
          <td style="color:#0f172a;">${escapeHtml(String(v))}</td>
        </tr>`,
      )
      .join("");
    infoRef.current.setContent(
      `<div style="font-family:system-ui,sans-serif;font-size:12px;max-width:260px;">
         <div style="font-weight:800;font-size:13px;margin-bottom:4px;">${escapeHtml(dev.name)}</div>
         <table style="border-collapse:collapse;font-size:11px;">${body}</table>
       </div>`,
    );
    infoRef.current.open({ map: mapRef.current, anchor: marker });
  }, []);

  const focusPlacemark = (dev) => {
    if (!mapRef.current) return;
    mapRef.current.panTo({ lat: dev.lat, lng: dev.lng });
    if (mapRef.current.getZoom() < 14) mapRef.current.setZoom(15);
    const marker = markersRef.current[dev.id];
    if (marker) openInfoFor(dev, marker);
  };

  const handleRefreshClick = () => {
    if (refreshFnRef.current) {
      refreshFnRef.current().catch(() => {});
    }
  };

  // v2.7.65+E56 · KPI drill-down click handlers ────────────────────────
  const openRosterIfClosed = () => {
    if (!rosterOpen) setRosterOpen(true);
  };
  // Total → reset all filters (broadest view).
  const onKpiTotalClick = () => {
    setStatusFilter("all");
    setFilterQuery("");
    setSelectedGroup("All");
    setHighlightId("");
    openRosterIfClosed();
  };
  // Active → toggle status filter between "active" and "all".
  const onKpiActiveClick = () => {
    setStatusFilter((s) => (s === "active" ? "all" : "active"));
    setHighlightId("");
    openRosterIfClosed();
  };
  // Avg Battery → flip to distance sort... actually more useful: highlight
  // the device with the lowest battery so the dispatcher sees who's about
  // to die first.
  const onKpiBatteryClick = () => {
    const withBattery = groupScoped
      .map((d) => {
        const m = d.battery && String(d.battery).match(/-?\d+(\.\d+)?/);
        return m ? { d, pct: parseFloat(m[0]) } : null;
      })
      .filter(Boolean);
    if (!withBattery.length) return;
    withBattery.sort((a, b) => a.pct - b.pct);
    const target = withBattery[0].d;
    focusPlacemark(target);
    setHighlightId(target.id);
    openRosterIfClosed();
  };
  // Longest Silence → jump to and highlight the specific device that's
  // been silent longest.
  const onKpiSilenceClick = () => {
    let worst = null;
    for (const d of groupScoped) {
      if (d.age_seconds == null) continue;
      if (!worst || d.age_seconds > worst.age_seconds) worst = d;
    }
    if (!worst) return;
    focusPlacemark(worst);
    setHighlightId(worst.id);
    openRosterIfClosed();
  };

  // Traffic layer lazy toggle. Google's TrafficLayer is created once and
  // reused via setMap(null | map) — cheap and idiomatic.
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    if (trafficLayerActive) {
      if (!trafficLayerRef.current) {
        trafficLayerRef.current = new window.google.maps.TrafficLayer();
      }
      trafficLayerRef.current.setMap(mapRef.current);
    } else if (trafficLayerRef.current) {
      trafficLayerRef.current.setMap(null);
    }
  }, [trafficLayerActive, status]);

  // Hide markers that don't belong to the selected group. Runs whenever the
  // group filter or the marker inventory changes.
  useEffect(() => {
    const visibleIds = new Set(groupScoped.map((d) => d.id));
    for (const [id, marker] of Object.entries(markersRef.current)) {
      marker.setVisible(visibleIds.has(id));
    }
  }, [groupScoped, roster]);

  return (
    <div
      ref={wrapperRef}
      className="relative w-full flex flex-col bg-zinc-950 border border-zinc-800"
      style={{ height: isFullscreen ? "100vh" : height }}
      data-testid="live-dispatch-map"
    >
      {/* Header strip — status pill + Roster + Fullscreen + Pop-Out */}
      <div className="flex items-center justify-between px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-zinc-100 select-none">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`h-2 w-2 rounded-full flex-shrink-0 ${dotClass}`}
            data-testid="live-map-status-dot"
            data-status={status}
          />
          <span className="text-[11px] font-semibold tracking-wide uppercase truncate">
            Live Fleet · FollowMee
          </span>
          <span className="hidden md:inline-flex items-center gap-1 text-[10px] text-zinc-400 font-mono" data-testid="live-map-vehicle-count">
            <Users className="h-3 w-3"/>
            {vehicleCount}
          </span>
          <span
            className="text-[10px] text-zinc-500 truncate hidden lg:inline"
            data-testid="live-map-status-message"
          >
            {message}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setRosterOpen((v) => !v)}
            className={`p-1.5 rounded hover:bg-zinc-800 transition inline-flex items-center gap-1 text-[10px] font-semibold ${rosterOpen ? "text-emerald-300 bg-zinc-800/60" : "text-zinc-300 hover:text-white"}`}
            title={rosterOpen ? "Hide fleet roster" : "Show fleet roster"}
            data-testid="live-dispatch-roster-toggle"
            aria-pressed={rosterOpen}
          >
            <LayoutPanelLeft className="h-3.5 w-3.5"/>
            <span className="hidden sm:inline">Roster</span>
          </button>
          {fullscreenEnabled && (
            <button
              type="button"
              onClick={handleFullscreenToggle}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-300 hover:text-white transition inline-flex items-center gap-1 text-[10px] font-semibold"
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              data-testid="live-dispatch-fullscreen-toggle"
            >
              {isFullscreen ? <Minimize2 className="h-3.5 w-3.5"/> : <Maximize2 className="h-3.5 w-3.5"/>}
              <span className="hidden sm:inline">{isFullscreen ? "Exit" : "Full"}</span>
            </button>
          )}
          {!isPopOutView && (
            <button
              type="button"
              onClick={handlePopOutWindow}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-300 hover:text-white transition inline-flex items-center gap-1 text-[10px] font-semibold"
              title="Pop out into isolated tracking window"
              data-testid="live-dispatch-popout-btn"
            >
              <ExternalLink className="h-3.5 w-3.5"/>
              <span className="hidden sm:inline">Pop Out</span>
            </button>
          )}
        </div>
      </div>

      {/* v2.7.65+E55 · Fleet at a Glance · 4-tile KPI strip
          v2.7.65+E56 · Click-through drill-down handlers */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800"
        data-testid="live-dispatch-fleet-kpis"
      >
        <KpiTile
          testid="live-dispatch-kpi-total"
          label="Total Devices"
          value={fleetKpis.total}
          accent="text-zinc-100"
          onClick={onKpiTotalClick}
          hintOverride={statusFilter === "all" && !filterQuery && selectedGroup === "All" ? null : "reset"}
          isActive={statusFilter === "all" && !filterQuery && selectedGroup === "All"}
        />
        <KpiTile
          testid="live-dispatch-kpi-active"
          label="Active Now"
          value={fleetKpis.active}
          hint={fleetKpis.total > 0 ? `${Math.round((fleetKpis.active / fleetKpis.total) * 100)}%` : null}
          accent="text-emerald-400"
          dotClass="bg-emerald-500 animate-pulse"
          onClick={onKpiActiveClick}
          isActive={statusFilter === "active"}
        />
        <KpiTile
          testid="live-dispatch-kpi-battery"
          label="Avg Battery"
          value={fleetKpis.avgBattery != null ? `${fleetKpis.avgBattery}%` : "—"}
          accent={
            fleetKpis.avgBattery == null ? "text-zinc-400" :
            fleetKpis.avgBattery >= 40 ? "text-emerald-400" :
            fleetKpis.avgBattery >= 20 ? "text-amber-400"   :
                                         "text-rose-400"
          }
          onClick={onKpiBatteryClick}
          hintOverride="lowest →"
        />
        <KpiTile
          testid="live-dispatch-kpi-silence"
          label="Longest Silence"
          value={fleetKpis.longestSilence != null ? formatDuration(fleetKpis.longestSilence) : "—"}
          accent={
            fleetKpis.longestSilence == null ? "text-zinc-400" :
            fleetKpis.longestSilence < 900   ? "text-emerald-400" :
            fleetKpis.longestSilence < 3600  ? "text-amber-400"   :
                                               "text-rose-400"
          }
          onClick={onKpiSilenceClick}
          hintOverride="who? →"
        />
      </div>

      {/* v2.7.65+E54 · Map dispatch toolbar — group filter · traffic layer
          · legend · refresh */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 bg-zinc-900/60 border-b border-zinc-800 text-zinc-200"
        data-testid="live-dispatch-toolbar"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Group</span>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="bg-zinc-950 text-zinc-100 border border-zinc-800 rounded px-2 py-1 text-[11px] focus:outline-none focus:border-emerald-500"
            data-testid="live-dispatch-toolbar-group-select"
          >
            <option value="All">All Groups</option>
            {uniqueGroups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <label
            className="flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer select-none"
            data-testid="live-dispatch-toolbar-traffic-label"
          >
            <input
              type="checkbox"
              checked={trafficLayerActive}
              onChange={(e) => setTrafficLayerActive(e.target.checked)}
              className="accent-amber-500"
              data-testid="live-dispatch-toolbar-traffic-toggle"
            />
            <TrafficCone className="h-3 w-3 text-amber-400"/>
            Traffic
          </label>

          <div
            className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[10px]"
            data-testid="live-dispatch-toolbar-legend"
          >
            <span className="inline-flex items-center gap-1 text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>
              Active
            </span>
            <span className="inline-flex items-center gap-1 text-rose-400">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500"/>
              Offline
            </span>
          </div>

          <button
            type="button"
            onClick={handleRefreshClick}
            disabled={refreshing}
            className="inline-flex items-center gap-1 bg-zinc-100 hover:bg-white text-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed px-2.5 py-1 rounded text-[11px] font-bold transition"
            title="Refresh fleet telemetry"
            data-testid="live-dispatch-toolbar-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`}/>
            Refresh
          </button>
        </div>
      </div>

      {/* Map canvas + optional Fleet Roster side panel */}
      <div className="flex-1 min-h-0 flex">
        <div
          ref={mapDivRef}
          className="flex-1 min-w-0 bg-zinc-950"
          data-testid="live-dispatch-map-canvas"
        />
        {rosterOpen && (
          <aside
            className="w-64 border-l border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden"
            data-testid="live-dispatch-roster-panel"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-zinc-900/50">
              <div className="flex items-center gap-1.5 min-w-0">
                <Users className="h-3.5 w-3.5 text-emerald-400"/>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-200">
                  Fleet Roster
                </span>
                <span className="text-[10px] font-mono text-zinc-500" data-testid="live-dispatch-roster-count">
                  {processedRoster.length}/{roster.length}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setRosterOpen(false)}
                className="p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-800"
                title="Hide roster"
                data-testid="live-dispatch-roster-close"
                aria-label="Hide roster"
              >
                <X className="h-3.5 w-3.5"/>
              </button>
            </div>

            {/* v2.7.65+E54 · Roster filter + sort */}
            <div
              className="flex items-center gap-1.5 px-3 py-2 border-b border-zinc-800/60 bg-zinc-950"
              data-testid="live-dispatch-roster-controls"
            >
              <Input
                type="text"
                placeholder="Search devices…"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className="h-7 flex-1 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 text-[11px]"
                data-testid="live-dispatch-roster-filter"
              />
              <button
                type="button"
                onClick={() => setSortMethod((m) => (m === "alpha" ? "distance" : "alpha"))}
                className="inline-flex items-center gap-1 h-7 px-2 rounded border border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-white hover:bg-zinc-800 text-[10px] font-semibold"
                title={sortMethod === "alpha" ? "Sort: A–Z (click for Nearest)" : "Sort: Nearest to center (click for A–Z)"}
                data-testid="live-dispatch-roster-sort"
              >
                {sortMethod === "alpha"
                  ? <><ArrowDownAZ className="h-3 w-3"/>A–Z</>
                  : <><Radar className="h-3 w-3 text-emerald-400"/>Nearest</>}
              </button>
            </div>

            <div className="text-[10px] px-3 py-1.5 text-zinc-500 border-b border-zinc-800/60 flex items-center gap-1" data-testid="live-dispatch-roster-lastupdate">
              Updated {lastRefreshAt ? relLastRefresh : "—"}
            </div>
            <div className="flex-1 overflow-y-auto">
              {processedRoster.length === 0 ? (
                <div className="px-3 py-4 text-[11px] text-zinc-500" data-testid="live-dispatch-roster-empty">
                  {roster.length === 0
                    ? (status === "loading" ? "Loading fleet…" : "No devices reported.")
                    : filterQuery
                      ? `No devices match "${filterQuery}".`
                      : "No devices in this group."}
                </div>
              ) : (
                <ul className="divide-y divide-zinc-800/70">
                  {processedRoster.map((dev, idx) => {
                    const isActive = dev.status === "active";
                    const distKm = sortMethod === "distance" ? haversineKm(mapCenter, dev) : null;
                    return (
                      <li key={dev.id}>
                        <button
                          type="button"
                          onClick={() => focusPlacemark(dev)}
                          className={`w-full text-left px-3 py-2 hover:bg-zinc-900 transition group ${
                            dev.id === highlightId
                              ? "bg-emerald-950/40 ring-1 ring-inset ring-emerald-500/60"
                              : ""
                          }`}
                          data-testid={`live-dispatch-roster-row-${idx}`}
                          data-highlight={dev.id === highlightId ? "on" : undefined}
                          title={`Click to center map on ${dev.name}`}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${isActive ? "bg-emerald-500" : "bg-rose-500"}`}
                              data-status={dev.status}
                            />
                            <span className="flex-1 min-w-0 text-[11px] font-bold text-zinc-100 truncate">
                              {dev.name}
                            </span>
                            <Crosshair className="h-3 w-3 text-zinc-500 group-hover:text-emerald-400 flex-shrink-0"/>
                          </div>
                          <div className="ml-3.5 mt-1 flex items-center gap-2 text-[10px] font-mono text-zinc-500 truncate">
                            <span>{dev.lat.toFixed(5)}, {dev.lng.toFixed(5)}</span>
                            {distKm != null && (
                              <span className="text-emerald-400/80">· {distKm.toFixed(1)} km</span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>
        )}
      </div>

      {status === "error" && (
        <div
          className="absolute bottom-2 right-2 left-2 md:left-auto md:max-w-md bg-rose-900/80 border border-rose-700 text-rose-100 text-[11px] px-3 py-2 rounded shadow"
          data-testid="live-map-error-banner"
        >
          {message}
        </div>
      )}
    </div>
  );
}

// Minimal HTML-escape for InfoWindow content — the KML `<description>`
// arrives as free-form text and MUST be escaped before dropping into
// innerHTML. Keep this tiny (no dep) since it's the only place we need it.
function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// "N s ago" / "N m ago" — used by the Fleet Roster "last updated" line.
function relSeconds(ts) {
  if (!ts) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// v2.7.65+E55 · Compact "12s / 4m / 2h 15m / 3d 4h" duration formatter
// used by the Longest Silence KPI. Input is seconds.
function formatDuration(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  if (s < 60)    return `${s}s`;
  if (s < 3600)  return `${Math.floor(s / 60)}m`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

// v2.7.65+E55 · KPI tile used by the Fleet at a Glance strip.
// v2.7.65+E56 · Now clickable for drill-down; isActive lights up the border.
function KpiTile({ testid, label, value, hint, accent, dotClass, onClick, isActive, hintOverride }) {
  const clickable = typeof onClick === "function";
  const displayHint = hintOverride ?? hint;
  const Wrapper = clickable ? "button" : "div";
  const wrapperProps = clickable
    ? {
        type: "button",
        onClick,
        "aria-pressed": !!isActive,
      }
    : {};
  return (
    <Wrapper
      className={`text-left bg-zinc-900/60 border rounded px-3 py-2 flex flex-col justify-center transition ${
        isActive
          ? "border-emerald-500 ring-1 ring-emerald-500/40"
          : "border-zinc-800"
      } ${clickable ? "hover:border-zinc-600 hover:bg-zinc-900 cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-500" : ""}`}
      data-testid={testid}
      {...wrapperProps}
    >
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
        {dotClass && <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`}/>}
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span
          className={`text-lg font-extrabold tabular-nums leading-none ${accent || "text-zinc-100"}`}
          data-testid={`${testid}-value`}
        >
          {value}
        </span>
        {displayHint && (
          <span className="text-[10px] text-zinc-500 font-mono" data-testid={`${testid}-hint`}>
            {displayHint}
          </span>
        )}
      </div>
    </Wrapper>
  );
}
